import * as THREE from "three";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
    vertexShader,
    fluidFragmentShader,
    displayFragmentShader,
} from "./shaders.js";

gsap.registerPlugin(ScrollTrigger);

// Desktop detection - disable WebGL on mobile/tablet
function shouldEnableEffect() {
    const minWidth = 1024;
    const isWideScreen = window.innerWidth >= minWidth;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return isWideScreen && !prefersReducedMotion;
}

const portraitTopUrl = "assets/images/hero_keyboard_top.webp";
const portraitBottomUrl = "assets/images/hero_keyboard_bottom_aligned.webp";

export function initHero() {
    const canvas = document.querySelector(".hero canvas");
    const heroSection = document.querySelector(".hero");
    if (!canvas || !heroSection) return;

    // =====================================================
    // PERFORMANCE: Skip WebGL on mobile/weak devices
    // =====================================================
    const enableWebGL = shouldEnableEffect();

    if (!enableWebGL) {
        // Hide WebGL canvas
        canvas.style.display = "none";

        // Create static background wrapper for shrink animation
        const staticWrapper = document.createElement("div");
        staticWrapper.className = "hero-static-wrapper";
        staticWrapper.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            width: 100%;
            height: 100%;
            transform: translate(-50%, -50%);
            transform-origin: center center;
            background-image: url(${portraitTopUrl});
            background-size: cover;
            background-position: center;
        `;
        heroSection.insertBefore(staticWrapper, heroSection.firstChild);

        // Calculate shrink values
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const targetSize = 300; // Smaller on mobile
        const squareSize = Math.min(vw, vh);
        const targetScale = targetSize / squareSize;
        let clipInsetX = 0, clipInsetY = 0;
        if (vw > vh) {
            clipInsetX = ((vw - vh) / 2 / vw) * 100;
        } else {
            clipInsetY = ((vh - vw) / 2 / vh) * 100;
        }

        // Mobile scroll animation with shrink
        const heroFooter = document.querySelector(".hero-footer");
        const fallbackTimeline = gsap.timeline({
            scrollTrigger: {
                trigger: heroSection,
                start: "top top",
                end: "+=100%",
                pin: true,
                scrub: 0.5,
                anticipatePin: 1,
            }
        });

        // Shrink animation for static wrapper
        fallbackTimeline.to(staticWrapper, {
            scale: targetScale,
            clipPath: `inset(${clipInsetY}% ${clipInsetX}% ${clipInsetY}% ${clipInsetX}% round 24px)`,
            ease: "none",
        }, 0);

        if (heroFooter) {
            fallbackTimeline.to(heroFooter, {
                opacity: 0,
                y: -20,
                ease: "none",
            }, 0);
        }
        return;
    }

    let renderer;

    // Try to create WebGL context with error handling
    try {
        renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: true,
            precision: "highp",
            powerPreference: "high-performance",
            failIfMajorPerformanceCaveat: false,
        });
    } catch (error) {
        console.warn("WebGL initialization failed:", error);
        // Fallback: hide canvas and show static content
        canvas.style.display = "none";
        return;
    }

    // Handle WebGL context loss
    canvas.addEventListener("webglcontextlost", (e) => e.preventDefault(), false);

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const mouse = new THREE.Vector2(0.5, 0.5);
    const prevMouse = new THREE.Vector2(0.5, 0.5);
    let isMoving = false;
    let lastMoveTime = 0;
    let mouseInCanvas = false;

    // =====================================================
    // PERFORMANCE: Reduced render target size (500 → 256)
    // Uses ~75% less GPU memory
    // =====================================================
    const size = 256;

    // =====================================================
    // PERFORMANCE: Use HalfFloatType instead of FloatType
    // Uses 50% less memory, sufficient for this effect
    // =====================================================
    const pingPongTargets = [
        new THREE.WebGLRenderTarget(size, size, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            type: THREE.HalfFloatType,
        }),
        new THREE.WebGLRenderTarget(size, size, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            type: THREE.HalfFloatType,
        }),
    ];
    let currentTarget = 0;

    const topTexture = createPlaceholderTexture("#0a0a0a"); // Dark background
    const bottomTexture = createPlaceholderTexture("#C4EF7A"); // Accent color

    const topTextureSize = new THREE.Vector2(1, 1);
    const bottomTextureSize = new THREE.Vector2(1, 1);

    const trailsMaterial = new THREE.ShaderMaterial({
        uniforms: {
            uPrevTrails: { value: null },
            uMouse: { value: mouse },
            uPrevMouse: { value: prevMouse },
            uResolution: { value: new THREE.Vector2(size, size) },
            uDecay: { value: 0.97 },
            uIsMoving: { value: false },
        },
        vertexShader,
        fragmentShader: fluidFragmentShader,
    });

    const displayMaterial = new THREE.ShaderMaterial({
        uniforms: {
            uFluid: { value: null },
            uTopTexture: { value: topTexture },
            uBottomTexture: { value: bottomTexture },
            uResolution: {
                value: new THREE.Vector2(window.innerWidth, window.innerHeight),
            },
            uDpr: { value: window.devicePixelRatio },
            uTopTextureSize: { value: topTextureSize },
            uBottomTextureSize: { value: bottomTextureSize },
        },
        vertexShader,
        fragmentShader: displayFragmentShader,
    });

    loadImage(portraitTopUrl, topTexture, topTextureSize);
    loadImage(portraitBottomUrl, bottomTexture, bottomTextureSize);

    const planeGeometry = new THREE.PlaneGeometry(2, 2);
    const displayMesh = new THREE.Mesh(planeGeometry, displayMaterial);
    scene.add(displayMesh);

    const simMesh = new THREE.Mesh(planeGeometry, trailsMaterial);
    const simScene = new THREE.Scene();
    simScene.add(simMesh);

    renderer.setRenderTarget(pingPongTargets[0]);
    renderer.clear();
    renderer.setRenderTarget(pingPongTargets[1]);
    renderer.clear();
    renderer.setRenderTarget(null);

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("resize", onWindowResize);

    // Cleanup function to prevent WebGL context accumulation
    function cleanup() {
        renderer.dispose();
        pingPongTargets[0].dispose();
        pingPongTargets[1].dispose();
        planeGeometry.dispose();
        trailsMaterial.dispose();
        displayMaterial.dispose();
    }

    // Cleanup on page unload to free WebGL resources
    window.addEventListener("beforeunload", cleanup);
    window.addEventListener("pagehide", cleanup);

    // Animation is started at the bottom of initHero() via startAnimation()

    // =====================================================
    // GSAP ScrollTrigger: Pin hero and shrink canvas
    // =====================================================
    const heroFooter = document.querySelector(".hero-footer");

    // Calculate target scale and clip-path for 500x500 square
    const targetSize = 500;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Calculate clip-path inset to create a square from the center
    // We need to calculate how much to cut from left/right or top/bottom
    let clipInsetX = 0;
    let clipInsetY = 0;

    if (vw > vh) {
        // Landscape: cut from left and right to make square
        clipInsetX = ((vw - vh) / 2 / vw) * 100;
    } else {
        // Portrait: cut from top and bottom to make square
        clipInsetY = ((vh - vw) / 2 / vh) * 100;
    }

    // Scale to achieve 500px from the square
    const squareSize = Math.min(vw, vh);
    const targetScale = targetSize / squareSize;

    // Center the canvas for scaling
    gsap.set(canvas, {
        position: "absolute",
        top: "50%",
        left: "50%",
        xPercent: -50,
        yPercent: -50,
        transformOrigin: "center center"
    });

    // Create a timeline for coordinated animations
    const heroTimeline = gsap.timeline({
        scrollTrigger: {
            trigger: heroSection,
            start: "top top",
            end: "+=100%",
            pin: true,
            scrub: 0.5,
            anticipatePin: 1,
        }
    });

    // Canvas shrink + square clip animation
    heroTimeline.to(canvas, {
        scale: targetScale,
        clipPath: `inset(${clipInsetY}% ${clipInsetX}% ${clipInsetY}% ${clipInsetX}% round 32px)`,
        ease: "none",
    }, 0);

    // Hero footer fade out animation
    if (heroFooter) {
        heroTimeline.to(heroFooter, {
            opacity: 0,
            y: -20,
            ease: "none",
        }, 0);
    }

    function createPlaceholderTexture(color) {
        const canvas = document.createElement("canvas");
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, 512, 512);

        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        return texture;
    }

    function loadImage(url, targetTexture, textureSizeVector) {
        const img = new Image();
        img.crossOrigin = "Anonymous";

        img.onload = function () {
            const originalWidth = img.width;
            const originalHeight = img.height;
            textureSizeVector.set(originalWidth, originalHeight);

            const maxSize = 4096;
            let newWidth = originalWidth;
            let newHeight = originalHeight;

            if (originalWidth > maxSize || originalHeight > maxSize) {
                if (originalWidth > originalHeight) {
                    newWidth = maxSize;
                    newHeight = Math.floor(originalHeight * (maxSize / originalWidth));
                } else {
                    newHeight = maxSize;
                    newWidth = Math.floor(originalWidth * (maxSize / originalHeight));
                }
            }

            const canvas = document.createElement("canvas");
            canvas.width = newWidth;
            canvas.height = newHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, newWidth, newHeight);

            const newTexture = new THREE.CanvasTexture(canvas);
            newTexture.minFilter = THREE.LinearFilter;
            newTexture.magFilter = THREE.LinearFilter;

            if (url.includes("top")) {
                displayMaterial.uniforms.uTopTexture.value = newTexture;
            } else {
                displayMaterial.uniforms.uBottomTexture.value = newTexture;
            }

            displayMaterial.needsUpdate = true;
        };

        img.onerror = function (err) {
            console.warn(`Texture not found: ${url}. Using placeholder.`, err);
        };

        img.src = url;
    }

    // =====================================================
    // PERFORMANCE: Aggressive mouse throttling (32ms = ~30fps)
    // =====================================================
    let lastMouseMoveTime = 0;
    const mouseThrottleMs = 32;

    function onMouseMove(event) {
        const now = performance.now();
        if (now - lastMouseMoveTime < mouseThrottleMs) return;
        lastMouseMoveTime = now;

        const canvasRect = canvas.getBoundingClientRect();

        prevMouse.copy(mouse);

        mouse.x = (event.clientX - canvasRect.left) / canvasRect.width;
        mouse.y = 1.0 - (event.clientY - canvasRect.top) / canvasRect.height;

        if (
            event.clientX >= canvasRect.left &&
            event.clientX <= canvasRect.right &&
            event.clientY >= canvasRect.top &&
            event.clientY <= canvasRect.bottom
        ) {
            mouseInCanvas = true;
            isMoving = true;
            lastMoveTime = performance.now();
        } else {
            mouseInCanvas = false;
            isMoving = false;
        }
    }

    function onTouchMove(event) {
        if (event.touches.length > 0) {
            // event.preventDefault(); // Don't prevent default scrolling if unnecessary

            const canvasRect = canvas.getBoundingClientRect();
            const touchX = event.touches[0].clientX;
            const touchY = event.touches[0].clientY;

            if (
                touchX >= canvasRect.left &&
                touchX <= canvasRect.right &&
                touchY >= canvasRect.top &&
                touchY <= canvasRect.bottom
            ) {
                prevMouse.copy(mouse);

                mouse.x = (touchX - canvasRect.left) / canvasRect.width;
                mouse.y = 1.0 - (touchY - canvasRect.top) / canvasRect.height;

                isMoving = true;
                lastMoveTime = performance.now();
            } else {
                isMoving = false;
            }
        }
    }

    function onWindowResize() {
        renderer.setSize(window.innerWidth, window.innerHeight);

        displayMaterial.uniforms.uResolution.value.set(
            window.innerWidth,
            window.innerHeight
        );
        displayMaterial.uniforms.uDpr.value = window.devicePixelRatio;
    }

    // =====================================================
    // PERFORMANCE: Animation pause when idle
    // Stops rendering after 2 seconds of inactivity
    // =====================================================
    let animationFrameId = null;
    let lastFrameTime = 0;
    const frameInterval = 1000 / 30; // 30 FPS
    let isAnimating = false;
    const idleTimeout = 2000; // Stop after 2 seconds idle

    function startAnimation() {
        if (!isAnimating) {
            isAnimating = true;
            lastFrameTime = performance.now();
            animationFrameId = requestAnimationFrame(animate);
        }
    }

    function stopAnimation() {
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }
        isAnimating = false;
    }

    function animate(currentTime) {
        // Check if should stop due to idle
        if (!isMoving && performance.now() - lastMoveTime > idleTimeout) {
            stopAnimation();
            return;
        }

        animationFrameId = requestAnimationFrame(animate);

        if (currentTime - lastFrameTime < frameInterval) return;
        lastFrameTime = currentTime;

        if (isMoving && performance.now() - lastMoveTime > 100) {
            isMoving = false;
        }

        const prevTarget = pingPongTargets[currentTarget];
        currentTarget = (currentTarget + 1) % 2;
        const currentRenderTarget = pingPongTargets[currentTarget];

        trailsMaterial.uniforms.uPrevTrails.value = prevTarget.texture;
        trailsMaterial.uniforms.uMouse.value.copy(mouse);
        trailsMaterial.uniforms.uPrevMouse.value.copy(prevMouse);
        trailsMaterial.uniforms.uIsMoving.value = isMoving;

        renderer.setRenderTarget(currentRenderTarget);
        renderer.render(simScene, camera);

        displayMaterial.uniforms.uFluid.value = currentRenderTarget.texture;

        renderer.setRenderTarget(null);
        renderer.render(scene, camera);
    }

    // Start animation on mouse enter/move
    canvas.addEventListener('mouseenter', () => {
        lastMoveTime = performance.now();
        startAnimation();
    });

    // Also start on scroll (for shrink effect visibility)
    window.addEventListener('scroll', () => {
        lastMoveTime = performance.now();
        startAnimation();
    }, { passive: true });

    // Initial render (just one frame, then stop)
    lastMoveTime = performance.now();
    startAnimation();
}
